import React from "react";

import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@replohq/lattice/components/tabs/Tabs";
import { cn } from "@replohq/lattice/utils/cn";
import { useAdminPanelActions } from "@v2/features/admin/adminPanelStore";
import { AgentBillingTab } from "@v2/features/admin/tabs/AgentBillingTab";
import { FeatureFlagsTab } from "@v2/features/admin/tabs/FeatureFlags";
import { LocalStorageTab } from "@v2/features/admin/tabs/LocalStorageTab";
import { MiscellaneousTab } from "@v2/features/admin/tabs/Miscellaneous";
import { OptionsTab } from "@v2/features/admin/tabs/Options";
import { QuickIdsTab } from "@v2/features/admin/tabs/QuickIds";
import { FloatingPanel } from "@v2/features/debug/FloatingPanel";
import { useIsDebugMode } from "@v2/hooks/useIsDebugMode";
import { useLocalStorageState } from "@v2/hooks/useLocalStorage";

const ADMIN_TABS = [
  {
    value: "feature-flags",
    label: "Feature Flags",
    content: <FeatureFlagsTab />,
  },
  { value: "options", label: "Options", content: <OptionsTab /> },
  {
    value: "miscellaneous",
    label: "Miscellaneous",
    content: <MiscellaneousTab />,
  },
  {
    value: "local-storage",
    label: "Local Storage",
    content: <LocalStorageTab />,
  },
  {
    value: "agent-billing",
    label: "Subscriptions",
    content: <AgentBillingTab />,
  },
  { value: "quick-ids", label: "Quick IDs", content: <QuickIdsTab /> },
] as const;

const AdminPanelInner = () => {
  const isDebugModeAllowed = useIsDebugMode();

  if (!isDebugModeAllowed) {
    return null;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col text-xs">
      <Tabs defaultValue="feature-flags" className="min-h-0 flex-1">
        <div className="w-full max-w-full shrink-0 overflow-x-auto">
          <TabsList variant="outline" size="sm" className="w-max min-w-full">
            {ADMIN_TABS.map(({ value, label }) => (
              <TabsTrigger key={value} size="sm" value={value}>
                <span className="text-xs">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        </div>
        {ADMIN_TABS.map(({ value, content }) => (
          <TabsContent key={value} value={value} className="min-h-0 flex-1">
            <div
              className={cn(
                "flex h-full min-h-0 flex-col overflow-y-auto p-3",
                value === "miscellaneous" && "gap-1",
              )}
            >
              {content}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export const AdminPanel = () => {
  const { close } = useAdminPanelActions();
  const [isMinimized, setIsMinimized] = useLocalStorageState(
    "replo.debug.adminPanel.minimized",
    false,
    { validate: (value): value is boolean => typeof value === "boolean" },
  );

  return (
    <FloatingPanel
      title="Admin Panel"
      storageKey="replo.debug.adminPanel"
      defaultSize={{ width: 700, height: 500 }}
      minSize={{ width: 400, height: 300 }}
      maxSize={{ width: 2000, height: 1200 }}
      onClose={close}
      onMinimize={() => setIsMinimized((prev) => !prev)}
      isMinimized={isMinimized ?? false}
    >
      <AdminPanelInner />
    </FloatingPanel>
  );
};
